import { Input, Selector } from '@casumo/dojo-react';
import { ChangeEvent, Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react';
import useFetchErrorByKey from '../api/hooks/useFetchErrorsByKey';
import Skeleton from '../design-system/skeleton';
import { useRegistrationStore } from '../store/store';
import { TIdentificationNumberESField } from '../types/types';
import { TEST_ID } from '../utils/constants';
import { GtmEvents } from '../utils/gtm/gtm';
import { checkIdentificationNumberES } from '../utils/utils';

const IdentificationNumberES = (data: TIdentificationNumberESField) => {
  const {
    errors,
    formData,
    metadata,
    setErrors,
    setFormData,
    setMetadata,
    setCustomValidations,
  } = useRegistrationStore();
  const getErrorByKey = useFetchErrorByKey;

  const invalidIdentificationNumberError = getErrorByKey('wrong_personal_number') ||
    'Número de identificación no válido';
  const emptyIdentificationNumberError = getErrorByKey('empty_personal_number') ||
    'Debes añadir tu DNI o NIE para continuar.';
  const allTypes = useMemo(() => data.selections.map(({ name }) => name), [data.selections]);
  const [selectedType, setSelectedType] = useState('');
  const [showImageSkeleton, setShowImageSkeleton] = useState(true);
  const [storeHasData, setStoreHasData] = useState(false);

  const selectedItem = useMemo(
    () => data.selections.find(({ name }) => name === selectedType),
    [selectedType, data.selections],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ [name]: value.toUpperCase() });
    setCustomValidations({ [name]: value ? '' : emptyIdentificationNumberError });
  }

  const handleBlur = useCallback(() => {
    if (!formData?.[selectedType]) {
      setCustomValidations({ [selectedType]: emptyIdentificationNumberError });
      setErrors({ [selectedType]: emptyIdentificationNumberError });

      return;
    }
    const isValid = checkIdentificationNumberES(formData?.[selectedType], selectedType);
    const errorMessage = isValid ? '' : invalidIdentificationNumberError;

    if (!isValid) GtmEvents.wrongFormatIdentificationNumberEs();

    setCustomValidations({ [selectedType]: errorMessage });
    setErrors({ [selectedType]: errorMessage });
  }, [
    formData,
    selectedType,
    invalidIdentificationNumberError,
    emptyIdentificationNumberError,
    setCustomValidations,
    setErrors,
  ]);

  const clearUserInfo = (type: string) => {
    const isDifferentType = selectedType !== type;
    setFormData({
      [selectedType]: '',
      ...(formData?.[type] === '' &&
        isDifferentType && {
        firstName:      '',
        lastName:       '',
        secondLastName: '',
      }),
    });
  };

  const handleSelectType = (type: string, shouldClearUserInfo: boolean) => {
    setCustomValidations({ [type]: emptyIdentificationNumberError, [selectedType]: '' });
    setErrors({ [selectedType]: '' });

    if (shouldClearUserInfo) {
      setCustomValidations({ secondLastName: '' });
      setErrors({ secondLastName: '' });
      clearUserInfo(type);
    } else {
      setFormData({ [selectedType]: '' });
    }

    setSelectedType(type);
    setMetadata({ identificationNumberTypeES: type });
  };

  const handleClear = () => {
    if (selectedItem) {
      setFormData({ [selectedItem.name]: '' });
      setErrors({ [selectedItem.name]: '' });
      setCustomValidations({ [selectedItem.name]: emptyIdentificationNumberError });
    }
  }

  useEffect(() => {
    if (formData?.[selectedType]) handleBlur();
  }, [metadata?.currentStep, storeHasData]);

  useEffect(() => {
    if (formData) {
      if (storeHasData) return;
      const existingIdentificationNumberType = allTypes.find((name) => !!formData?.[name]);
      setCustomValidations({ [data.selections[0].name]: '' })
      handleSelectType(existingIdentificationNumberType ?? data.selections[0].name, false)
      setStoreHasData(true);
    } else {
      setCustomValidations({ [selectedType]: emptyIdentificationNumberError });
    }
  }, [metadata?.currentStep]);

  return (
    <>
      {selectedItem && (
        <Fragment key={selectedItem.name}>
          <div className="flex items-center gap-xs bg-grey-0 p-xs rounded-lg">
            <div className="relative w-[53px] h-[36px]">
              {showImageSkeleton && (
                <Skeleton
                  className="absolute top-0 left-0 w-full h-full"
                  testId={TEST_ID.IMAGE + '-skeleton'}
                />
              )}
              <img
                src={data.documentImage}
                alt={selectedItem.name}
                className={`w-[53px] h-[36px] transition-opacity duration-300 ${
                  showImageSkeleton ? 'opacity-0' : 'opacity-100'
                }`}
                onLoad={() => setShowImageSkeleton(false)}
                onError={() => setShowImageSkeleton(false)}
                data-testid={TEST_ID.IMAGE + '-' + selectedItem.name}
              />
            </div>
            <div className="leading-lg text-xs text-grey-90"
              data-testid={TEST_ID.DESCRIPTION + selectedItem.name}
            >
              {selectedItem.documentDescription}
            </div>
          </div>
        </Fragment>
      )}

      <Selector name="identificationNumberSelector">
        {data.selections.map(({ name }) => (
          <Selector.Radio
            key={name}
            className="text-center has-[:checked]:bg-purple-0 has-[:checked]:text-purple-80"
            value={name}
            name={name}
            checked={selectedType === name}
            onChange={() => handleSelectType(name, true)}
            data-testid={TEST_ID.SELECT + name}
          >
            {name}
          </Selector.Radio>
        ))}
      </Selector>

      {selectedItem && (
        <Fragment key={selectedItem.name + 'input'}>
          <Input
            id="identificationNumber"
            name={selectedItem.name}
            label={selectedItem.labelInput}
            placeholder={selectedItem.labelInput}
            value={formData?.[selectedItem.name] || ''}
            onChange={handleInputChange}
            onBlur={handleBlur}
            error={Boolean(errors[selectedItem.name])}
            data-error={Boolean(errors[selectedItem.name])}
            errorMessage={errors[selectedItem.name]}
            clearable={true}
            onClear={handleClear}
            data-testid={TEST_ID.INPUT + selectedItem.name}
            wrapperClass="hide-close-button-on-blur"
          />
          <div className="text-grey-50 leading-lg text-xs">
            {selectedItem.example}
          </div>
        </Fragment>
      )}
    </>
  );
};

export default memo(IdentificationNumberES);
